import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { mq } from '../../../theme/scalingTokens';

export const useFormFieldStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    minWidth: 0,
    position: 'relative',
  },
  /**
   * Read-only count hint anchored to the top-right of the field
   * (e.g. "1 day" beside `Meeting Date(s)`, "1 hour/day" beside `Meeting time`).
   * Sits above Fluent's `<Field>` content without affecting label layout, so it
   * works identically in BookingForm and the inline editor on Available Spaces.
   */
  fieldHint: {
    position: 'absolute',
    top: 0,
    right: 0,
    fontSize: fontScale.caption,
    lineHeight: 1.4,
    color: tokens.colorNeutralForeground3,
    pointerEvents: 'none',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  figmaInputs: {
    rowGap: tokens.spacingVerticalXS,
  },
  figmaLabel: {
    fontSize: fontScale.body,
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalXXS,
  },
  figmaHelperText: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground3,
  },
  requiredAsterisk: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  matchDateInputWidth: {
    width: 'calc((100% - 1.5rem) / 2)',
    maxWidth: '100%',
    minWidth: '6rem',
    flexShrink: 1,
    boxSizing: 'border-box',
    [mq.smallTablet]: {
      width: `calc(100% - ${tokens.spacingHorizontalS})`,
      minWidth: 0,
      alignSelf: 'flex-start',
    },
  },
  matchDateInputControl: {
    maxWidth: '100px',
    boxSizing: 'border-box',
  },
  policyBanner: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    minWidth: 0,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteYellowBorder1),
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalM,
    '& .fui-Checkbox__label': {
      fontSize: fontScale.body,
    },
  },
  salmonBanner: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    minWidth: 0,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteYellowBorder1),
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalM,
    '& .fui-Checkbox__label': {
      fontSize: fontScale.body,
    },
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: fontScale.caption,
    lineHeight: tokens.lineHeightBase200,
  },
  checkboxLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  helpLink: {
    color: tokens.colorBrandForegroundLink,
    fontSize: fontScale.caption,
    cursor: 'pointer',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  helpTooltip: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    fontSize: fontScale.caption,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground1,
    maxWidth: '17.5rem',
    position: 'absolute' as const,
    zIndex: 1000,
  },
  contactListRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto',
    gap: tokens.spacingHorizontalM,
    alignItems: 'start',
    [mq.mobile]: {
      gridTemplateColumns: '1fr',
    },
  },
  contactDeleteBtn: {
    color: tokens.colorPaletteRedForeground1,
    cursor: 'pointer',
    alignSelf: 'center',
    marginTop: tokens.spacingVerticalXL,
  },
  addContactBtn: {
    alignSelf: 'flex-start',
    marginTop: tokens.spacingVerticalS,
  },
  timeRangeRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    minWidth: 0,
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalS,
    // Smaller flex-basis lets two date/time pickers fit on a single line in
    // the narrower fields column at desktop widths; min-width keeps each
    // input readable, and wrap kicks in only when there is genuinely no room.
    '& > *:not(span)': {
      flex: '1 1 9rem',
      minWidth: '7rem',
      flexShrink: 1,
    },
    [mq.tablet]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      rowGap: tokens.spacingVerticalS,
      '& > *:not(span)': {
        flex: '1 0 auto',
        minWidth: 0,
      },
    },
  },
  separator: {
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
    flexShrink: 0,
    [mq.mobile]: {
      display: 'none',
    },
  },
});
